import type { VoidFunctionComponent, ReactNode } from 'react';

import Select from '../Select/Select';
import { FormLabel, FormLabelVariant } from '../Forms';
import type { SelectOption } from '../../../types';

type Props = {
  //  Label for the multiselect.
  label: ReactNode;
  //  This is all of the options which are selectable. Note you can use `selectOptionFactory` to create SelectOptions easily.
  options: Array<SelectOption<string | number>>;
  //  Values are the values which are currently selected.
  values: Array<SelectOption<string | number>>;
  //  Change handler. Gets all selected options after the change takes effect.
  onChange: (values: Array<SelectOption<string | number>>) => void;
  //  If it is disabled or not.
  isDisabled?: boolean;
  //  If the search feature of the dropdown should be included or not.
  isSearchable?: boolean;
   // Whether to show the dropdown when the element is focused. When this disabled the user cannot add new items. Users will still be able to remove selected items from the visible list.
  menuIsOpen?: boolean;
  //  Optional placeholder text
  placeholder?: string;
  //  ID for testing purposes.
  testId?: string;
  //  Optional text to display next to the label (usually the word "required").
  requiredText?: ReactNode;
};

/**
 * FilterCardMultiSelect is a Select dropdown which allows you to
 * select multiple options. This serves as a slim wrapper around
 * a FormLabel + Select component, making it easier to use this
 * in the context of a FilterCard.
 */
const FilterCardMultiSelect: VoidFunctionComponent<Props> = ({
  label,
  placeholder,
  options,
  values,
  onChange,
  isDisabled,
  isSearchable,
  menuIsOpen,
  testId,
  requiredText,
}) => (
  <FormLabel
    label={label}
    variant={FormLabelVariant.Filter}
    requiredText={requiredText}
    data-test-id={testId}
  >
    <Select
      isMulti
      isClearable
      menuIsOpen={menuIsOpen}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      placeholder={placeholder}
      value={values}
      options={options}
      onChange={onChange}
    />
  </FormLabel>
);

export default FilterCardMultiSelect;
